/**
 * The records a conversation works on, grouped by type, each opening in place (owner 2026-10-31: "we need the side
 * panel to support all referenced entities as a list, rendered within the side panel on expand"). The server reads them
 * from the conversation's typed parts and saved artifacts (`GET /conversations/{id}/entities`); this lists the types the
 * surface can open for this person (`useEntityOpener`). Demo page: /chats (the context column's lists), as `ItemList`
 * groups.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Layers } from 'lucide-react';
import type React from 'react';
import { useEffect } from 'react';
import {
  listConversationEntitiesOptions,
  listConversationEntitiesQueryKey,
} from '../../../generated/api/@tanstack/react-query.gen.js';
import type { ConversationEntity } from '../../../generated/api/types.gen.js';
import { PAGE_HELP } from '../../../help/index.js';
import { Callout } from '../../ui/Callout.js';
import { EmptyState } from '../../ui/EmptyState.js';
import { ItemList } from '../../ui/ItemList.js';
import { SkeletonList } from '../../ui/Skeleton.js';
import { Button } from '../../ui/Button.js';
import { useEntityOpener } from './entity-opener.js';

interface ConversationEntitiesListProps {
  conversationId: string;
  /** The newest message's id: the list is read again when a new message lands. */
  newestMessageId?: string | null;
}

export const ConversationEntitiesList: React.FC<ConversationEntitiesListProps> = ({
  conversationId,
  newestMessageId = null,
}) => {
  const opener = useEntityOpener();
  const queryClient = useQueryClient();
  const query = useQuery(listConversationEntitiesOptions({ path: { id: conversationId } }));
  const help = PAGE_HELP['conversation-records'];

  // A turn that shows a new record adds it; the list follows the conversation as it grows.
  useEffect(() => {
    if (!newestMessageId) return;
    void queryClient.invalidateQueries({
      queryKey: listConversationEntitiesQueryKey({ path: { id: conversationId } }),
    });
  }, [newestMessageId, conversationId, queryClient]);

  if (query.isPending) return <SkeletonList count={3} label="Loading what this conversation works on" />;
  if (query.isError) {
    return (
      <Callout
        tone="danger"
        title="Could not load what this conversation works on."
        actions={
          <Button size="sm" variant="secondary" onClick={() => void query.refetch()}>
            Retry
          </Button>
        }
      />
    );
  }

  const groups = new Map<string, ConversationEntity[]>();
  for (const entity of query.data) {
    const label = opener?.labelOf(entity.entity) ?? null;
    if (!label) continue;
    groups.set(label, [...(groups.get(label) ?? []), entity]);
  }

  if (groups.size === 0) {
    return (
      <EmptyState
        icon={Layers}
        title={help?.emptyState.title ?? 'Nothing here yet'}
        description={help?.emptyState.description ?? ''}
      />
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {[...groups.entries()].map(([label, entities]) => (
        <section key={label} aria-label={label}>
          <h3 className="mb-2 font-mono text-[10px] uppercase tracking-[1.1px] text-faint">
            {label} · {entities.length}
          </h3>
          <ItemList
            items={entities.map((entity) => ({
              id: `${entity.entity}:${entity.id}`,
              title: entity.title,
              ...(entity.subtitle ? { subtitle: entity.subtitle } : {}),
              onPress: () => opener?.open(entity),
              pressLabel: `Open ${entity.title}`,
            }))}
          />
        </section>
      ))}
    </div>
  );
};
